import { useCallback, useRef } from 'react';

import { Control, FieldValues, Path, PathValue, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { renderSectionDividerHeading } from '@/modules/common/components/molecules/selectors/single-select/single-select-section-heading';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import {
  renderProjectDropdownOption,
  renderProjectDropdownTrigger,
} from '@/modules/projects/helpers/project-dropdown-option';
import { useTimeTrackerProjects } from '@/modules/projects/hooks/use-time-tracker-projects';

const BOOKED_GROUP_KEY = 'booked';
const OTHER_GROUP_KEY = 'other';

interface TimeTrackerProjectSelectProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Pre-filled item for edit forms — also suppresses auto-prefill of a single booked project. */
  initialSelectedItem?: DropdownSearchItem;
  /** Date the time entry is being logged for — the booking check window is centered on it. */
  anchorDate: Date;
  onSelect?: (item: DropdownSearchItem | null) => void;
}

/**
 * Project picker for the Time Tracking modal. Composes `SingleSelectWithPagination`
 * with the `/api/projects/time-tracker/search` endpoint (via `useTimeTrackerProjects`):
 * projects the member is booked on are grouped under a "Booked" section ahead of the
 * rest, and when the member has exactly one booked project on first load, it's
 * selected automatically.
 */
export function TimeTrackerProjectSelect<T extends FieldValues>({
  name,
  control,
  setValue,
  label,
  placeholder,
  disabled,
  initialSelectedItem,
  anchorDate,
  onSelect,
}: TimeTrackerProjectSelectProps<T>) {
  const { t } = useTranslation('authenticated.timeTracking');
  const fieldValue = useWatch({ control, name });
  // Latches after the first `onDataLoad` call (always the initial, non-search, first
  // page — see SingleSelectWithPagination's query fn) so later pages/searches never
  // re-trigger the auto-prefill check.
  const hasCheckedAutoPrefillRef = useRef(false);

  const { queryKey, staleTime, loadItems, searchItems } = useTimeTrackerProjects(anchorDate);

  const getGroupKey = useCallback(
    (item: DropdownSearchItem) => (item.isInPrimaryGroup ? BOOKED_GROUP_KEY : OTHER_GROUP_KEY),
    []
  );

  const renderGroupHeading = useCallback(
    (groupKey: string) =>
      renderSectionDividerHeading(
        groupKey === BOOKED_GROUP_KEY
          ? t('modal.addEntry.fields.projectPicker.bookedSectionLabel')
          : t('modal.addEntry.fields.projectPicker.otherSectionLabel')
      ),
    [t]
  );

  const handleDataLoad = useCallback(
    (items: DropdownSearchItem[]) => {
      if (hasCheckedAutoPrefillRef.current) return;
      hasCheckedAutoPrefillRef.current = true;

      // Don't override an explicit initial project (edit forms) or a value the
      // user already picked before the first page finished loading.
      if (initialSelectedItem || fieldValue) return;

      const bookedItems = items.filter(item => item.isInPrimaryGroup);
      if (bookedItems.length !== 1) return;

      const [bookedProject] = bookedItems;
      setValue(name, bookedProject.id as PathValue<T, Path<T>>, { shouldValidate: true });
      onSelect?.(bookedProject);
    },
    [initialSelectedItem, fieldValue, setValue, name, onSelect]
  );

  return (
    <SingleSelectWithPagination
      name={name}
      control={control}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      isClearSelectionEnabled={false}
      queryKey={queryKey}
      staleTime={staleTime}
      loadItems={loadItems}
      searchItems={searchItems}
      onSelect={onSelect}
      onDataLoad={handleDataLoad}
      initialSelectedItem={initialSelectedItem}
      fetchBeforeOpen
      getGroupKey={getGroupKey}
      renderGroupHeading={renderGroupHeading}
      renderOption={(option, isSelected) =>
        renderProjectDropdownOption(option, { showCheck: true, isSelected })
      }
      renderTrigger={renderProjectDropdownTrigger}
    />
  );
}
